import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

export interface ItDeclarationItem {
  id: string;
  referenceNumber: string;
  declarationType: string;
  requestType: string;
  creationDate: string;
  employeeName: string;
  requestPhase: string;
  requestedValue: number;
  approvedValue: number;
  taxExemptApproved: string;
  approver: string;
  workflowId: string;
  currency: string;
  status: 'In-Process' | 'Completed' | 'To Be Processed';
  statusCode: string;
  raw?: any;
}

export const toDisplayString = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') return String(value.name ?? value.code ?? value.value ?? '');
  return String(value);
};

const IT_DECLARATION_PHASE_TEXT: Record<string, string> = {
  P: 'Pending Approval',
};

export const mapItDeclarationItem = (item: any): ItDeclarationItem => {
  const phaseSource =
    item?.requestPhase ?? item?.RequestPhase ?? item?.PhaseOfTheRequest1 ??
    item?.ToTransactionDatas?.PhaseOftheRequest;
  const phaseDisplay = toDisplayString(phaseSource);
  const phaseRaw = IT_DECLARATION_PHASE_TEXT[phaseDisplay] ?? phaseDisplay;
  const statusRaw = toDisplayString(item?.requestStatus ?? item?.RequestStatus ?? item?.Status);
  const status: ItDeclarationItem['status'] =
    /complete/i.test(phaseRaw) || /complete/i.test(statusRaw)
      ? 'Completed'
      : /process|pending/i.test(phaseRaw)
      ? 'In-Process'
      : 'To Be Processed';

  const creationDateRaw =
    item?.RequestCreationDate ?? item?.CreationDate ?? item?.creationDate;
  let creationDate = '';
  if (creationDateRaw) {
    const parsed = new Date(creationDateRaw);
    if (!isNaN(parsed.getTime())) {
      const pad = (n: number) => String(n).padStart(2, '0');
      creationDate = `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}.${parsed.getFullYear()}`;
    } else {
      creationDate = String(creationDateRaw);
    }
  }

  return {
    id: toDisplayString(item?.id ?? item?.ReferenceNumber ?? item?.referenceNumber),
    referenceNumber: toDisplayString(item?.ReferenceNumber ?? item?.referenceNumber),
    declarationType: toDisplayString(
      item?.declarationType ?? item?.ITDeclarationType ?? item?.ToTransactionDatas?.ReimbursementType,
    ),
    requestType: toDisplayString(
      item?.requestType ?? item?.RequestType ?? item?.ToTransactionDatas?.RequestType,
    ),
    creationDate,
    employeeName: toDisplayString(item?.employeeName ?? item?.EmployeeName ?? item?.EmpID ?? item?.EmpId),
    requestPhase: phaseRaw,
    requestedValue: Number(item?.RequestedAmount ?? item?.requestedAmount ?? 0),
    approvedValue: Number(item?.ApprovedAmount ?? item?.approvedAmount ?? 0),
    taxExemptApproved: toDisplayString(item?.ApprovedTaxExempt ?? item?.approvedTaxExempt),
    approver: toDisplayString(item?.approverName ?? item?.ApproverName),
    workflowId: toDisplayString(item?.WorkflowID ?? item?.workflowId),
    currency: toDisplayString(item?.Currency ?? item?.currency) || 'INR',
    status,
    statusCode: statusRaw,
    raw: item,
  };
};

export const fetchItDeclarationList = createAsyncThunk(
  "itDeclaration/fetchList",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.FIND_ALL, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationById = createAsyncThunk(
  "itDeclaration/fetchById",
  async (referenceNumber: string, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.FIND_ONE, { referenceNumber });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationTypes = createAsyncThunk(
  "itDeclaration/fetchTypes",
  async (payload: { empId?: string; forceSync?: boolean } = {}, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.REQUEST_TYPES_SYNC, {
        empId: payload.empId || "",
        forceSync: payload.forceSync ?? true,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationSubTypes = createAsyncThunk(
  "itDeclaration/fetchSubTypes",
  async (payload: { sync?: boolean; reimbursementType: string }, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.SUB_TYPES, {
        sync: payload.sync ?? true,
        reimbursementType: payload.reimbursementType,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationPropertyTypes = createAsyncThunk(
  "itDeclaration/fetchPropertyTypes",
  async (payload: { reimbursementType?: string; subType: string; sync?: boolean }, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.PROPERTY_TYPES, {
        reimbursementType: payload.reimbursementType || "IOS",
        subType: payload.subType,
        sync: payload.sync ?? true,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationRequestPhase = createAsyncThunk(
  "itDeclaration/fetchRequestPhase",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.REQUEST_PHASE, {});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationRequestStatus = createAsyncThunk(
  "itDeclaration/fetchRequestStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.REQUEST_STATUS, {});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationConfigSets = createAsyncThunk(
  "itDeclaration/fetchConfigSets",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.CONFIGURATION_SETS, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationContentSets = createAsyncThunk(
  "itDeclaration/fetchContentSets",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.CONTENT_SETS, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const submitItDeclaration = createAsyncThunk(
  "itDeclaration/submit",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.CREATE_OR_UPDATE, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const syncItDeclaration = createAsyncThunk(
  "itDeclaration/sync",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.SYNC_REQUEST, {});
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteItDeclaration = createAsyncThunk(
  "itDeclaration/delete",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.CREATE_OR_UPDATE, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchItDeclarationAttachments = createAsyncThunk(
  "itDeclaration/fetchAttachments",
  async (referenceNumber: string, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.GET_ATTACHMENTS, { referenceNumber });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const downloadForm12BB = createAsyncThunk(
  "itDeclaration/downloadForm12BB",
  async (payload: { empId?: string; referenceNumber: string }, { rejectWithValue }) => {
    try {
      const response = await Api.post(API_URLS.IT_DECLARATION.DOWNLOAD_FORM_12BB, payload);
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);


interface ItDeclarationState {
  list: ItDeclarationItem[];
  selectedRequest: ItDeclarationItem | null;
  declarationTypes: any[];
  subTypes: any[];
  propertyTypes: any[];
  phaseOptions: any[];
  statusOptions: any[];
  configSets: any | null;
  contentSets: any | null;
  attachments: any[];
  loading: boolean;
  submitting: boolean;
  syncing: boolean;
  error: string | null;
}

const initialState: ItDeclarationState = {
  list: [],
  selectedRequest: null,
  declarationTypes: [],
  subTypes: [],
  propertyTypes: [],
  phaseOptions: [],
  statusOptions: [],
  configSets: null,
  contentSets: null,
  attachments: [],
  loading: false,
  submitting: false,
  syncing: false,
  error: null,
};

const extractArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
};

export const itDeclarationSlice = createSlice({
  name: "itDeclaration",
  initialState,
  reducers: {
    clearSelectedRequest: (state) => {
      state.selectedRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch List
      .addCase(fetchItDeclarationList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItDeclarationList.fulfilled, (state, action) => {
        state.loading = false;
        const payloadData = action.payload?.data?.data || action.payload?.data || action.payload;
        const rawList = Array.isArray(payloadData) ? payloadData : [];
        state.list = rawList.map(mapItDeclarationItem);
      })
      .addCase(fetchItDeclarationList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch list";
      })
      .addCase(fetchItDeclarationById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchItDeclarationById.fulfilled, (state, action) => {
        state.loading = false;
        const raw = action.payload?.data ?? action.payload ?? null;
        if (raw) {
          state.selectedRequest = mapItDeclarationItem(raw);
        }
      })
      .addCase(fetchItDeclarationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch request details";
      })
      .addCase(fetchItDeclarationTypes.fulfilled, (state, action) => {
        state.declarationTypes = extractArray(action.payload);
      })
      .addCase(fetchItDeclarationSubTypes.fulfilled, (state, action) => {
        state.subTypes = extractArray(action.payload);
      })
      .addCase(fetchItDeclarationPropertyTypes.fulfilled, (state, action) => {
        state.propertyTypes = extractArray(action.payload);
      })
      .addCase(fetchItDeclarationRequestPhase.fulfilled, (state, action) => {
        state.phaseOptions = extractArray(action.payload);
      })
      .addCase(fetchItDeclarationRequestStatus.fulfilled, (state, action) => {
        state.statusOptions = extractArray(action.payload);
      })
      .addCase(fetchItDeclarationConfigSets.fulfilled, (state, action) => {
        state.configSets = action.payload ?? null;
      })
      .addCase(fetchItDeclarationContentSets.fulfilled, (state, action) => {
        state.contentSets = action.payload ?? null;
      })
      .addCase(fetchItDeclarationAttachments.fulfilled, (state, action) => {
        const body = action.payload?.data ?? action.payload ?? {};
        state.attachments = Array.isArray(body?.ToAttachments?.results)
          ? body.ToAttachments.results
          : Array.isArray(body?.ToAttachments)
          ? body.ToAttachments
          : Array.isArray(body)
          ? body
          : [];
      })
      .addCase(submitItDeclaration.pending, (state) => {
        state.submitting = true;
      })
      .addCase(submitItDeclaration.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(submitItDeclaration.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message || "Submission failed";
      })
      .addCase(syncItDeclaration.pending, (state) => {
        state.syncing = true;
      })
      .addCase(syncItDeclaration.fulfilled, (state) => {
        state.syncing = false;
      })
      .addCase(syncItDeclaration.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.error.message || "Sync failed";
      })
      .addCase(deleteItDeclaration.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deleteItDeclaration.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(deleteItDeclaration.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message || "Delete failed";
      });
  },
});

export const { clearSelectedRequest } = itDeclarationSlice.actions;
export default itDeclarationSlice.reducer;
